import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Briefcase, Plus, Pencil, Trash2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ITEMS_PER_PAGE = 10;

export const Actividades = () => {
  const { isAdmin } = useAuth();
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingActividad, setEditingActividad] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: ''
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, itemId: null, deps: [], loadingDeps: false });

  useEffect(function() {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      var res = await axios.get(API + '/actividades');
      setActividades(res.data);
    } catch (error) {
      toast.error('Error al cargar actividades');
    } finally {
      setLoading(false);
    }
  }

  function getFilteredActividades() {
    var result = [];
    var searchLower = search.toLowerCase();
    for (var i = 0; i < actividades.length; i++) {
      var act = actividades[i];
      if (act.nombre.toLowerCase().indexOf(searchLower) >= 0) {
        result.push(act);
      }
    }
    return result;
  }

  // Reset to page 1 when search changes
  useEffect(function() {
    setCurrentPage(1);
  }, [search]);

  function getPaginatedActividades(filtered) {
    var startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    var endIndex = startIndex + ITEMS_PER_PAGE;
    return filtered.slice(startIndex, endIndex);
  }

  function getTotalPages(filtered) {
    return Math.ceil(filtered.length / ITEMS_PER_PAGE);
  }

  function openCreateDialog() {
    setEditingActividad(null);
    setFormData({ nombre: '', descripcion: '' });
    setIsDialogOpen(true);
  }

  function openEditDialog(actividad) {
    setEditingActividad(actividad);
    setFormData({
      nombre: actividad.nombre,
      descripcion: actividad.descripcion || ''
    });
    setIsDialogOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      if (editingActividad) {
        await axios.put(API + '/actividades/' + editingActividad.id, formData);
        toast.success('Actividad actualizada');
      } else {
        await axios.post(API + '/actividades', formData);
        toast.success('Actividad creada');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  }

  async function handleDelete(id) {
    setDeleteDialog(prev => ({ ...prev, open: true, itemId: id, deps: [], loadingDeps: true }));
    try {
      const res = await axios.get(`${API}/dependencias/actividades/${id}`);
      setDeleteDialog(prev => ({ ...prev, deps: res.data.dependencias, loadingDeps: false }));
    } catch { setDeleteDialog(prev => ({ ...prev, loadingDeps: false })); }
  }

  async function confirmDelete() {
    try {
      await axios.delete(API + '/actividades/' + deleteDialog.itemId);
      toast.success('Actividad eliminada');
      setDeleteDialog({ open: false, itemId: null, deps: [], loadingDeps: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
      setDeleteDialog({ open: false, itemId: null, deps: [], loadingDeps: false });
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  var filteredActividades = getFilteredActividades();
  var totalPages = getTotalPages(filteredActividades);
  var paginatedActividades = getPaginatedActividades(filteredActividades);
  var startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  var endItem = Math.min(currentPage * ITEMS_PER_PAGE, filteredActividades.length);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Actividades</h1>
          <p className="text-slate-500">Gestión de actividades para asignación de capacitaciones</p>
        </div>
        {isAdmin() && (
          <Button 
            onClick={openCreateDialog}
            className="bg-teal-500 hover:bg-teal-600"
            data-testid="create-actividad-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Actividad
          </Button>
        )}
      </div>

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar actividad..."
                value={search}
                onChange={function(e) { setSearch(e.target.value); }}
                className="pl-9"
                data-testid="search-actividades"
              />
            </div>
            <span className="text-sm text-slate-500">{filteredActividades.length} actividades</span>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Actividad</TableHead>
                <TableHead>Descripción</TableHead>
                {isAdmin() && <TableHead className="text-right">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedActividades.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-slate-500 py-8">
                    No hay actividades registradas
                  </TableCell>
                </TableRow>
              ) : (
                paginatedActividades.map(function(actividad) {
                  return (
                    <TableRow key={actividad.id} data-testid={'actividad-row-' + actividad.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                            <Briefcase className="w-4 h-4 text-amber-600" />
                          </div>
                          {actividad.nombre}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 max-w-md truncate">
                        {actividad.descripcion || '-'}
                      </TableCell>
                      {isAdmin() && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={function() { openEditDialog(actividad); }}
                              data-testid={'edit-actividad-' + actividad.id}
                            >
                              <Pencil className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={function() { handleDelete(actividad.id); }}
                              data-testid={'delete-actividad-' + actividad.id}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="text-sm text-slate-500">
                Mostrando {startItem} - {endItem} de {filteredActividades.length} actividades
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={function() { setCurrentPage(currentPage - 1); }}
                  disabled={currentPage === 1}
                  data-testid="pagination-prev"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Anterior
                </Button>
                <span className="text-sm text-slate-600 px-2">
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={function() { setCurrentPage(currentPage + 1); }}
                  disabled={currentPage === totalPages}
                  data-testid="pagination-next"
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingActividad ? 'Editar Actividad' : 'Nueva Actividad'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre de la Actividad *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={function(e) { setFormData({ ...formData, nombre: e.target.value }); }}
                required
                data-testid="actividad-nombre-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                value={formData.descripcion}
                onChange={function(e) { setFormData({ ...formData, descripcion: e.target.value }); }}
                rows={3}
                data-testid="actividad-descripcion-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={function() { setIsDialogOpen(false); }}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-teal-500 hover:bg-teal-600" data-testid="save-actividad-btn">
                {editingActividad ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={deleteDialog.open}
        onOpenChange={(v) => !v && setDeleteDialog({ open: false, itemId: null, deps: [], loadingDeps: false })}
        onConfirm={confirmDelete}
        title="Eliminar Actividad"
        description="¿Esta seguro de eliminar esta actividad? Esta accion no se puede deshacer."
        dependencies={deleteDialog.deps}
        loadingDeps={deleteDialog.loadingDeps}
      />
    </div>
  );
};
